// Which cells of a table artifact a chat citation cited (#12818). The
// citation carries the cited figure (or, for a chip with none, the figures
// of the paragraph around it), and the table pane marks every cell holding
// one. The matching is the #12452 cell-source check run the other way round:
// the figure matcher finds the figure in the cell's text, or
// `passageSpelling` finds the cell's value in the figure (commas, currency,
// percent, exponent form, and the same digits a thousand, million or billion
// apart). One rule of its own on top: a figure with a magnitude suffix and at
// least three significant digits matches a value that rounds to it at that
// scale (`$130.3M` for 130303576). A reworded or computed value does not
// match. A cited name matches a text cell that reads exactly that name, case
// and spacing aside.
import {
  figureHits,
  figureRegex,
} from "../common/utils/addOnServices/figureMatch";
import { decimalOf, roundHalfUp, shift } from "./decimalText";
import { passageSpelling } from "./passageSpelling";
import {
  displayCell,
  isRecordCell,
  type TableCell,
  type TableColumn,
  type TableSheet,
} from "./tableArtifact";

/** The cited cells of one sheet, by `row:key`. */
export type CitedCellIds = ReadonlySet<string>;

export const citedCellId = (rowIndex: number, key: string): string =>
  `${rowIndex}:${key}`;

const collapse = (text: string): string =>
  text.toLowerCase().replace(/\s+/g, " ").trim();

// A number as a table cell stores it, exponent form included.
const NUMBER = /^[-+]?\d+(?:\.\d+)?(?:e[-+]?\d+)?$/i;

// A percent cell (`12.5%`): `passageSpelling` finds it as the percentage
// spelling of a cited fraction (`0.125`, #13498).
const PERCENT_CELL = /^[-+]?\d+(?:\.\d+)?\s?%$/;

// A figure a reply rounds to a magnitude: sign or accounting paren, an
// optional currency, the digits, and the magnitude, spelled or abbreviated.
const SCALED_FIGURE =
  /^(\()?\s*([-−])?\s*[$€£¥]?\s*([-−])?\s*(\d[\d,]*(?:\.\d+)?)\s*(k|thousand|m|mm|mn|million|b|bn|billion)\s*\)?$/i;

const MAGNITUDE: Record<string, number> = {
  k: 3,
  thousand: 3,
  m: 6,
  mm: 6,
  mn: 6,
  million: 6,
  b: 9,
  bn: 9,
  billion: 9,
};

// Fewer significant digits than this and a rounding match is a coincidence
// waiting to happen: `$2.1B` is every value from 2.05 to 2.15 billion. The
// same floor as `passageSpelling`'s unit conversion (#12452).
const MIN_ROUNDED_DIGITS = 3;

interface ScaledFigure {
  negative: boolean;
  /** The figure's own digits, as `roundHalfUp` spells them: `130.3`. */
  digits: string;
  decimals: number;
  exponent: number;
}

const parseScaled = (figure: string): ScaledFigure | null => {
  const parts = figure.trim().match(SCALED_FIGURE);
  if (!parts) return null;
  const [, paren, signBefore, signAfter, raw, unit] = parts;
  const written = decimalOf(raw.replace(/,/g, ""));
  if (!written) return null;
  const significant = (written.int + written.frac).replace(/^0+/, "");
  if (significant.length < MIN_ROUNDED_DIGITS) return null;
  return {
    negative: Boolean(paren || signBefore || signAfter),
    digits: roundHalfUp(written, written.frac.length),
    decimals: written.frac.length,
    exponent: MAGNITUDE[unit.toLowerCase()],
  };
};

/** Whether `figure` reads as a number (and so matches numbers, never
 *  words). */
export const isFigure = (figure: string): boolean =>
  figureRegex(figure) !== null || parseScaled(figure) !== null;

/** The cell's value rounded to the figure's precision at the figure's
 *  magnitude is the figure: 130303576 is `$130.3M`. */
const roundsTo = (value: string, scaled: ScaledFigure): boolean => {
  if (!NUMBER.test(value)) return false;
  const negative = value.startsWith("-");
  if (negative !== scaled.negative) return false;
  const magnitude = decimalOf(value.replace(/^[-+]/, ""));
  if (!magnitude) return false;
  const shifted = shift(magnitude, -scaled.exponent);
  // Below the magnitude it names, a figure is not a rounding of the value:
  // `$100.5M` is not 130.
  if (shifted.int === "0") return false;
  return roundHalfUp(shifted, scaled.decimals) === scaled.digits;
};

/** One cited value, parsed once for every cell it is matched against. */
type Citation =
  | {
      kind: "figure";
      figure: string;
      regex: RegExp | null;
      scaled: ScaledFigure | null;
    }
  | { kind: "name"; name: string };

const citationOf = (cited: string): Citation | null => {
  const regex = figureRegex(cited);
  const scaled = parseScaled(cited);
  if (regex !== null || scaled !== null)
    return { kind: "figure", figure: cited, regex, scaled };
  const name = collapse(cited);
  return name === "" ? null : { kind: "name", name };
};

const cellIsCited = (
  raw: TableCell,
  column: TableColumn,
  citations: readonly Citation[]
): boolean => {
  if (raw === null || raw === "") return false;
  const text = displayCell(raw, column.type);
  const value = isRecordCell(raw) ? text : String(raw);
  const trimmed = text.trim();
  return citations.some((citation) => {
    if (citation.kind === "name") {
      // A name: the whole cell, never a number column.
      return column.type !== "number" && collapse(text) === citation.name;
    }
    // The figure as the cell prints it, filling the cell: a cell that only
    // mentions the figure among other words is not the cited value.
    if (
      figureHits(citation.regex, trimmed).some(
        (hit) => hit.text.trim() === trimmed
      )
    )
      return true;
    if (!NUMBER.test(value) && !PERCENT_CELL.test(value)) return false;
    return (
      passageSpelling(value, citation.figure) !== null ||
      (citation.scaled !== null && roundsTo(value, citation.scaled))
    );
  });
};

/** Every cited cell, per sheet in the workbook's order. */
export const citedCellsBySheet = (
  sheets: readonly TableSheet[],
  cited: readonly string[] | undefined
): CitedCellIds[] => {
  const citations = (cited ?? [])
    .map(citationOf)
    .filter((citation): citation is Citation => citation !== null);
  return sheets.map((sheet) => {
    const ids = new Set<string>();
    if (citations.length === 0) return ids;
    sheet.rows.forEach((row, rowIndex) => {
      for (const column of sheet.columns) {
        if (cellIsCited(row[column.key] ?? null, column, citations))
          ids.add(citedCellId(rowIndex, column.key));
      }
    });
    return ids;
  });
};

/** The first cited cell in reading order (row, then column) of the first
 *  sheet that holds one, or null. */
export const firstCitedCell = (
  sheets: readonly TableSheet[],
  cells: readonly CitedCellIds[]
): { sheet: number; id: string } | null => {
  for (let index = 0; index < sheets.length; index += 1) {
    const ids = cells[index];
    if (!ids || ids.size === 0) continue;
    const sheet = sheets[index];
    for (let row = 0; row < sheet.rows.length; row += 1) {
      for (const column of sheet.columns) {
        const id = citedCellId(row, column.key);
        if (ids.has(id)) return { sheet: index, id };
      }
    }
  }
  return null;
};

// A figure in prose: an optional sign or paren, currency, the digits, a
// fraction, and a unit or magnitude.
const PROSE_FIGURE =
  /(?<![\w.,$€£¥])\(?[-−]?[$€£¥]?\d[\d,]*(?:\.\d+)?(?:\s?(?:%|bps|bn|mm|mn|thousand|million|billion|[mkbx])(?![a-z]))?\)?/gi;
// A bare year: "in 2024" would mark every year in the table.
const BARE_YEAR = /^(19|20)\d\d$/;
// What makes a figure worth matching on its own: a currency, a unit or
// magnitude, a decimal point or a thousands separator. Without one, three
// digits at least: a bare "3" or "31" in a paragraph is a count or a day,
// and would mark every small number in the table.
const MARKED = /[$€£¥%.,a-z]/i;
const MIN_BARE_DIGITS = 3;

/** The figures of the paragraph around a citation, in order, for a chip
 *  that names no figure of its own. Words are never taken. */
export const figuresInProse = (prose: string): string[] => {
  const found: string[] = [];
  for (const match of prose.matchAll(PROSE_FIGURE)) {
    let figure = match[0].replace(/,+$/, "");
    // A paren is the figure's only when it closes it: "(about $5M" is not.
    if (figure.startsWith("(") !== figure.endsWith(")"))
      figure = figure.replace(/^\(|\)$/g, "");
    if (BARE_YEAR.test(figure) || !isFigure(figure)) continue;
    const digits = figure.replace(/\D/g, "").length;
    if (!MARKED.test(figure) && digits < MIN_BARE_DIGITS) continue;
    if (!found.includes(figure)) found.push(figure);
  }
  return found;
};
